import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { LabeledSelect } from '../../../../shared/labeled-select/LabeledSelect';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { BlueContainedButton } from '../../../../shared/button/blue-contained-button/BlueContainedButton';
import { ScenarioPicker } from './ScenarioPicker';
import { LimitsBanner } from './LimitsBanner';
import { SandboxRunsNote } from './SandboxRunsNote';
import {
    createRun,
    fetchRunToCopy,
    fetchScenarios,
    fetchSimulationLimits
} from '../redux/runs/actions';
import { fetchWorkspaces } from '../redux/workspaces/actions';
import {
    getLimitsState,
    getRunState,
    getRunToCopyState,
    getScenariosState,
    getWorkspacesState
} from '../redux/selectors';
import { redirectTo } from '../../../../routing/redirectService';
import {
    getDefaultParameterValues,
    getScenarioName,
    isValidSeed,
    planRunAgain,
    toScenarioRequests,
    validateParameterValue
} from '../simulation.model';
import { SimulationPath } from '../simulation.paths';

export const makeWorkspaceOptions = workspaces =>
    _.map(workspaces, workspace => ({
        value: workspace.code,
        label: `${workspace.code} · copy of ${workspace.sourceCode}`
    }));

class NewRunContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            workspace: null,
            seed: '',
            label: '',
            // The values of the parameters of the scenarios that are chosen, by the id of the scenario
            selected: {},
            // What the form was filled in from, when it is a run again
            copied: null
        };
        this.copyApplied = false;
    }

    /* The run to play again, from the address: /simulations/new?from=<id> */
    get runToCopyId() {
        const location = this.props.location;
        const from = new URLSearchParams(location && location.search).get(
            'from'
        );

        return from || null;
    }

    componentDidMount() {
        this.props.fetchWorkspaces();
        this.props.fetchScenarios();
        this.props.fetchSimulationLimits();
        if (this.runToCopyId) {
            this.props.fetchRunToCopy({ runId: this.runToCopyId });
        }
        // Everything may be there already, when the form is opened again
        this.fillInFromRunToCopy();
    }

    componentDidUpdate() {
        this.fillInFromRunToCopy();
    }

    /* Once the run, the sandboxes and the catalog are all there: the form is filled in once, and then left to the user. */
    fillInFromRunToCopy() {
        const {
            runToCopy,
            workspaces,
            workspacesLoaded,
            scenarios,
            scenariosLoaded
        } = this.props;

        if (
            this.copyApplied ||
            !this.runToCopyId ||
            !runToCopy ||
            String(runToCopy.id) !== String(this.runToCopyId) ||
            !workspacesLoaded ||
            !scenariosLoaded
        ) {
            return;
        }
        this.copyApplied = true;
        const plan = planRunAgain(runToCopy, workspaces, scenarios);

        this.setState({
            workspace: plan.workspace,
            seed: plan.seed,
            label: plan.label,
            selected: plan.selected,
            copied: { run: runToCopy, plan }
        });
    }

    scenariosById = () => _.keyBy(this.props.scenarios, 'id');

    toggleScenario = id =>
        this.setState(({ selected }) => ({
            selected: _.has(selected, id)
                ? _.omit(selected, id)
                : {
                      ...selected,
                      [id]: getDefaultParameterValues(this.scenariosById()[id])
                  }
        }));

    changeValue = (id, key, value) =>
        this.setState(({ selected }) => ({
            selected: { ...selected, [id]: { ...selected[id], [key]: value } }
        }));

    /* The chosen scenarios in the order of the catalog, with the values of their parameters. */
    chosenScenarios = () =>
        _.filter(this.props.scenarios, s => _.has(this.state.selected, s.id));

    hasInvalidParameter = () =>
        _.some(this.chosenScenarios(), scenario =>
            _.some(scenario.parameters, parameter =>
                validateParameterValue(
                    parameter,
                    this.state.selected[scenario.id][parameter.key]
                )
            )
        );

    canStart = () => {
        const { limits, submitting } = this.props;
        const { workspace, seed } = this.state;

        return (
            !!workspace &&
            !submitting &&
            !_.isEmpty(this.chosenScenarios()) &&
            !this.hasInvalidParameter() &&
            isValidSeed(seed) &&
            (!limits || limits.enabled)
        );
    };

    start = () => {
        const { workspace, seed, label } = this.state;
        const chosen = this.chosenScenarios().map(scenario => ({
            id: scenario.id,
            values: this.state.selected[scenario.id]
        }));

        this.props.createRun({
            workspace,
            seed: seed === '' ? undefined : seed,
            label: _.trim(label) || undefined,
            scenarios: toScenarioRequests(chosen, this.scenariosById())
        });
    };

    renderWorkspace() {
        const { workspaces, workspacesLoaded } = this.props;

        if (!workspacesLoaded) {
            return <SpinLoader />;
        }
        if (_.isEmpty(workspaces)) {
            return (
                <div className={'sim-banner warning'}>
                    <i className={'material-icons'}>warning</i>
                    <div>
                        There is no sandbox to play in yet.{' '}
                        <Link to={SimulationPath.WORKSPACES}>
                            Create a sandbox
                        </Link>{' '}
                        first.
                    </div>
                </div>
            );
        }
        return (
            <div>
                <LabeledSelect
                    className={'sim-field'}
                    label={'Sandbox to play in'}
                    options={makeWorkspaceOptions(workspaces)}
                    value={this.state.workspace}
                    onChange={workspace => this.setState({ workspace })}
                />
                <SandboxRunsNote
                    limits={this.props.limits}
                    workspace={_.find(workspaces, {
                        code: this.state.workspace
                    })}
                    onOpenRuns={() => redirectTo(SimulationPath.RUNS)}
                />
            </div>
        );
    }

    renderScenarios() {
        const { scenarios, scenariosLoaded } = this.props;

        return !scenariosLoaded ? (
            <SpinLoader />
        ) : (
            <ScenarioPicker
                scenarios={scenarios}
                selected={this.state.selected}
                onToggle={this.toggleScenario}
                onChangeValue={this.changeValue}
            />
        );
    }

    renderCopied() {
        const { copied } = this.state;

        if (!copied) {
            return null;
        }
        const { run, plan } = copied;

        return (
            <div className={'sim-banner info'}>
                <i className={'material-icons'}>content_copy</i>
                <div>
                    Filled in from run #{run.id}:{' '}
                    {plan.parametersKept
                        ? 'the same seed and the same values of the parameters, so the same players are played.'
                        : 'the same seed, so the same players are played where the scenarios are played with the same values. This run did not keep the values of the parameters of its scenarios, so they start at their defaults.'}
                    {!_.isEmpty(plan.repeated) && (
                        <div>
                            The run played{' '}
                            {_.map(plan.repeated, id =>
                                getScenarioName(this.scenariosById(), id)
                            ).join(', ')}{' '}
                            more than once, with their own values. The form has
                            each scenario once, so the first values are filled
                            in and the players will not be the same.
                        </div>
                    )}
                    {!_.isEmpty(plan.unknownParameters) && (
                        <div>
                            Parameters the scenarios do not have any more are
                            left out: {plan.unknownParameters.join(', ')}.
                        </div>
                    )}
                    {plan.workspaceMissing && (
                        <div>
                            The sandbox {run.workspace} does not exist any more:
                            choose another.
                        </div>
                    )}
                    {!_.isEmpty(plan.missing) && (
                        <div>
                            Not offered any more, or not available now:{' '}
                            {_.map(plan.missing, id =>
                                getScenarioName(this.scenariosById(), id)
                            ).join(', ')}
                            .
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { limits, refusal } = this.props;
        const { seed, label } = this.state;

        return (
            <div className={'sim-new-run sim-form'}>
                {this.renderCopied()}
                <LimitsBanner
                    limits={limits}
                    blockingRunId={refusal && refusal.activeRunId}
                    onOpenRun={runId => redirectTo(SimulationPath.run(runId))}
                />

                <div className={'sim-section'}>
                    <ContentBox
                        title={'Where'}
                        content={this.renderWorkspace()}
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'What to play'}
                        subtitle={`${_.size(this.state.selected) ||
                            'None'} chosen`}
                        content={this.renderScenarios()}
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Options'}
                        content={
                            <div className={'sim-field-row'}>
                                <div className={'sim-field'}>
                                    <span className={'input-label'}>Label</span>
                                    <input
                                        type={'text'}
                                        value={label}
                                        placeholder={'Optional'}
                                        onChange={event =>
                                            this.setState({
                                                label: event.target.value
                                            })
                                        }
                                    />
                                </div>
                                <div className={'sim-field'}>
                                    <span className={'input-label'}>Seed</span>
                                    <input
                                        type={'text'}
                                        className={
                                            isValidSeed(seed) ? '' : 'invalid'
                                        }
                                        value={seed}
                                        placeholder={'Picked for you'}
                                        onChange={event =>
                                            this.setState({
                                                seed: event.target.value
                                            })
                                        }
                                    />
                                    {isValidSeed(seed) ? (
                                        <span className={'sim-hint'}>
                                            The same seed plays the same players
                                            and events.
                                        </span>
                                    ) : (
                                        <span className={'sim-error'}>
                                            A seed is a whole number.
                                        </span>
                                    )}
                                </div>
                            </div>
                        }
                    />
                </div>

                <div className={'sim-actions-row'}>
                    <TransparentButton
                        onClick={() => redirectTo(SimulationPath.RUNS)}
                    >
                        Cancel
                    </TransparentButton>
                    <BlueContainedButton
                        text={this.props.submitting ? 'Starting…' : 'Start run'}
                        disabled={!this.canStart()}
                        onClick={this.start}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const workspaces = getWorkspacesState(state);
    const scenarios = getScenariosState(state);

    return {
        workspaces: workspaces.data,
        workspacesLoaded: workspaces.loaded,
        scenarios: scenarios.data,
        scenariosLoaded: scenarios.loaded,
        limits: getLimitsState(state).data,
        runToCopy: getRunToCopyState(state).data,
        submitting: getRunState(state).submitting,
        refusal: getRunState(state).refusal
    };
};

const mapDispatchToProps = dispatch => ({
    fetchWorkspaces: () => dispatch(fetchWorkspaces()),
    fetchScenarios: () => dispatch(fetchScenarios()),
    fetchSimulationLimits: () => dispatch(fetchSimulationLimits()),
    fetchRunToCopy: args => dispatch(fetchRunToCopy(args)),
    createRun: args => dispatch(createRun(args))
});

export default connect(mapStateToProps, mapDispatchToProps)(NewRunContainer);
